import unescapedText from 'I18n/utils/unescapedText';
import { getInvoiceAssociatedObjectProperty } from './property';
import formatName from 'I18n/utils/formatName';
import { isValidEmail } from './validation';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import PortalIdParser from 'PortalIdParser';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { downloadInvoice } from '../api/InvoicesAPI';
import { getBrandingDetails } from '../api/BrandingAPI';
import InvoicePreviewIframe from '../components/InvoicePreviewIframe';
import { FETCH_INVOICE_EMAIL_SETTINGS } from '../graphQL/invoiceEmailQueries';
import { Button } from 'foundations-components/transitional/components/Button';
import { WarningIcon } from 'foundations-assets';
import { jsx as _jsx } from "react/jsx-runtime";
export const rateLimitExceededErrorStatus = 429;
export const getEmailContactValue = contact => contact.objectId || contact.properties.email.value;
export const getPersonalEmailFromValues = connectedInboxes => {
  const hasViewPermissions = connectedInbox => connectedInbox.inboxStatus === 'INACTIVE' || connectedInbox.inboxStatus === 'ENABLED';
  const isSharedConnectedInbox = connectedInbox => connectedInbox.shared;
  const getPrimaryAliasAsFromValue = connectedInbox => {
    const primaryAlias = connectedInbox.aliases.find(alias => alias.primary);
    return {
      disabled: connectedInbox.inboxStatus === 'INACTIVE',
      name: primaryAlias.resolvedFromName || undefined,
      email: primaryAlias.sendFromEmail || connectedInbox.accountId,
      shared: connectedInbox.shared,
      conversationsInboxName: primaryAlias.conversationsInboxName,
      accountId: connectedInbox.accountId,
      fallback: false
    };
  };
  return connectedInboxes.filter(hasViewPermissions).filter(connectedInbox => !isSharedConnectedInbox(connectedInbox)).map(getPrimaryAliasAsFromValue);
};
export const getTeamFromValues = (connectedAccounts, defaultEmail) => {
  const hasViewPermissionsOrIsDefaultEmail = connectedAccount => connectedAccount.userAllowedAccess || !connectedAccount.isDefault && connectedAccount.email === defaultEmail;
  const isFallbackEmail = connectedAccount => connectedAccount.isDefault;
  const mapFromValue = connectedAccount => ({
    email: connectedAccount.sendFromEmail || connectedAccount.email,
    name: connectedAccount.resolvedFromName || undefined,
    disabled: !connectedAccount.isActive,
    shared: true,
    conversationsInboxName: connectedAccount.conversationsInboxName,
    accountId: connectedAccount.email,
    fallback: connectedAccount.isDefault
  });
  return connectedAccounts.filter(connectedAccount => hasViewPermissionsOrIsDefaultEmail(connectedAccount)).filter(connectedAccount => !isFallbackEmail(connectedAccount)).map(mapFromValue);
};
export const getEmailOptionDisplayValue = ({
  name,
  email
}) => {
  if (name && email) {
    return `${name} (${email})`;
  } else if (email) {
    return `${email}`;
  } else {
    return `${name}`;
  }
};
export const getFromValuesGroupedSelectOptions = fromValues => {
  const personalEmails = fromValues.filter(fromValue => !fromValue.shared);
  const teamEmails = fromValues.filter(fromValue => fromValue.shared && !fromValue.fallback);
  const groupedFromEmails = [personalEmails.length > 0 && {
    text: unescapedText('invoices.components.sendInvoiceModal.fields.from.personalEmailsSubheader'),
    options: personalEmails.map(fromValue => ({
      text: getEmailOptionDisplayValue({
        name: fromValue.name,
        email: fromValue.email
      }),
      value: fromValue.email
    }))
  }, teamEmails.length > 0 && {
    text: unescapedText('invoices.components.sendInvoiceModal.fields.from.teamEmailsSubheader'),
    options: teamEmails.map(fromValue => ({
      text: getEmailOptionDisplayValue({
        name: fromValue.name,
        email: fromValue.email
      }),
      value: fromValue.email,
      help: fromValue.conversationsInboxName
    }))
  }].filter(maybeGroupedSelectOption => Boolean(maybeGroupedSelectOption));
  return groupedFromEmails;
};
export const getOptionsFromContacts = (contacts, optionDisplayNameFormatter = getEmailOptionDisplayValue) => contacts.map(contact => {
  const email = contact.properties.email && contact.properties.email.value;
  const name = formatName({
    firstName: getInvoiceAssociatedObjectProperty(contact, 'firstname'),
    lastName: getInvoiceAssociatedObjectProperty(contact, 'lastname')
  });
  const text = optionDisplayNameFormatter({
    name,
    email
  });
  const value = contact.objectId || email;
  const shouldShowWarning = !email || !isValidEmail(email);
  return {
    text,
    value,
    tagUse: shouldShowWarning ? 'danger' : undefined,
    icon: shouldShowWarning ? WarningIcon : undefined
  };
});
export const getOptions = (input, contacts, optionDisplayNameFormatter) => {
  const sanitizedInput = input.trim();
  const doesInputMatchContact = Boolean(contacts.find(contact => contact.properties.email && contact.properties.email.value.toLowerCase() === sanitizedInput));
  const options = getOptionsFromContacts(contacts, optionDisplayNameFormatter);
  const shouldShowWarning = !isValidEmail(sanitizedInput);
  if (doesInputMatchContact) {
    return options;
  } else {
    return [...options, {
      text: sanitizedInput,
      value: sanitizedInput,
      tagUse: shouldShowWarning ? 'danger' : undefined,
      icon: shouldShowWarning ? WarningIcon : undefined
    }];
  }
};
export const getNewEmailContact = value => ({
  objectId: null,
  objectTypeId: CONTACT_TYPE_ID,
  properties: {
    email: {
      name: 'email',
      value
    }
  },
  portalId: PortalIdParser.get()
});
export const getInitialEmailSubject = (isUpdateFromInvoiceEditor, senderCompanyName) => {
  if (isUpdateFromInvoiceEditor) {
    if (senderCompanyName) {
      return unescapedText('invoices.components.sendInvoiceModal.fields.subject.initial.update', {
        senderCompanyName
      });
    } else {
      return unescapedText('invoices.components.sendInvoiceModal.fields.subject.initial.updateNoSenderCompany');
    }
  } else {
    if (senderCompanyName) {
      return unescapedText('invoices.components.sendInvoiceModal.fields.subject.initial.default', {
        senderCompanyName
      });
    } else {
      return unescapedText('invoices.components.sendInvoiceModal.fields.subject.initial.defaultNoSenderCompany');
    }
  }
};
export const getDownloadInvoiceLink = invoice => {
  if (!invoice.objectId) {
    throw new Error("Invoice doesn't have an objectId");
  }
  return downloadInvoice(invoice.objectId).then(response => {
    if (!response.url) {
      throw new Error('Download invoice link not found');
    }
    return response.url;
  });
};
export const mapContactToEmailContact = contact => {
  return {
    portalId: contact.portalId,
    objectId: contact.objectId,
    objectTypeId: CONTACT_TYPE_ID,
    properties: {
      email: contact.properties.email,
      firstname: contact.properties.firstname,
      lastname: contact.properties.lastname
    }
  };
};
export const getEmailSettingsConfig = () => {
  return {
    query: FETCH_INVOICE_EMAIL_SETTINGS,
    canSendFromOtherEmailsDisabledTooltip: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "invoices.components.sendInvoiceModal.fields.from.allowOtherEmailsDisabledTooltip"
    })
  };
};
export const getEmailShareTabInitialValues = (contacts, isUpdateFromInvoiceEditor) => {
  const initialToContact = contacts.length > 0 ? mapContactToEmailContact(contacts[0]) : undefined;
  const initialCcContacts = contacts.slice(1).map(mapContactToEmailContact);
  const getEmailSubject = () => {
    return getBrandingDetails().then(brandingDetails => getInitialEmailSubject(isUpdateFromInvoiceEditor, brandingDetails.companyName));
  };
  return {
    toContact: initialToContact,
    ccContacts: initialCcContacts,
    subject: getEmailSubject,
    body: ''
  };
};
export const getLinkOrPdfShareTabConfig = (invoice, isUngatedForInvoicePreview = false) => {
  var _invoice$properties$h;
  const invoiceLink = (_invoice$properties$h = invoice.properties['hs_invoice_link']) === null || _invoice$properties$h === void 0 ? void 0 : _invoice$properties$h.value;
  return {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "invoices.components.sendInvoiceModal.linkOrPdfShareTab.title"
    }),
    description: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "invoices.components.sendInvoiceModal.linkOrPdfShareTab.description"
    }),
    previewComponent: /*#__PURE__*/_jsx(InvoicePreviewIframe, {
      invoice: invoice,
      shouldFetchInvoice: true,
      isUngatedForInvoicePreview: isUngatedForInvoicePreview
    }),
    previewScale: 0.8,
    copyLinkProps: {
      getCopyLink: () => {
        return Promise.resolve(invoiceLink);
      }
    },
    downloadPdfProps: {
      getPdfLink: () => getDownloadInvoiceLink(invoice),
      failedAlertTitle: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoices.components.sendInvoiceModal.linkOrPdfShareTab.downloadPdfAlert.title"
      }),
      failedAlertMessage: refetchLink => /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "invoices.components.sendInvoiceModal.linkOrPdfShareTab.downloadPdfAlert.message_jsx",
        elements: {
          Link: Button
        },
        options: {
          linkProps: {
            use: 'link',
            onClick: () => refetchLink()
          }
        }
      })
    }
  };
};
export const getSendEmailError = error => {
  if (error.status === rateLimitExceededErrorStatus) {
    return {
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoices.alerts.sendEmail.rateLimitExceededError.titleText"
      }),
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoices.alerts.sendEmail.rateLimitExceededError.message"
      })
    };
  }
  return {
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "invoices.alerts.sendEmail.error.titleText"
    }),
    message: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "invoices.alerts.sendEmail.error.message"
    })
  };
};